<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const { t } = useI18n();

const allLabels = useMapGetter('labels/getLabels');
const labelUiFlags = useMapGetter('labels/getUIFlags');
const contactLabels = useMapGetter('contactLabels/getContactLabels');
const contactLabelUiFlags = useMapGetter('contactLabels/getUIFlags');

const contactId = computed(() => props.selectedContact?.id);
const selectedLabelTitles = computed(() =>
  contactLabels.value(contactId.value)
);

const sortedLabels = computed(() =>
  [...allLabels.value].sort((a, b) => {
    const aSelected = selectedLabelTitles.value.includes(a.title);
    const bSelected = selectedLabelTitles.value.includes(b.title);

    if (aSelected !== bSelected) return Number(bSelected) - Number(aSelected);
    return a.title.localeCompare(b.title);
  })
);

const isFetching = computed(
  () => labelUiFlags.value.isFetching || contactLabelUiFlags.value.isFetching
);

const isUpdating = computed(() => contactLabelUiFlags.value.isUpdating);

const hasLabels = computed(() => sortedLabels.value.length > 0);

const fetchLabels = async () => {
  if (!allLabels.value.length) {
    store.dispatch('labels/get');
  }

  if (contactId.value) {
    await store.dispatch('contactLabels/get', contactId.value);
  }
};

const isLabelSelected = label =>
  selectedLabelTitles.value.includes(label.title);

const toggleLabel = async label => {
  if (!contactId.value || isUpdating.value) return;

  const currentLabels = selectedLabelTitles.value;
  const updatedLabels = isLabelSelected(label)
    ? currentLabels.filter(labelTitle => labelTitle !== label.title)
    : [...currentLabels, label.title];

  try {
    await store.dispatch('contactLabels/update', {
      contactId: contactId.value,
      labels: updatedLabels,
    });
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.LABELS.API.UPDATE_ERROR'));
  }
};

watch(contactId, (newContactId, oldContactId) => {
  if (newContactId && newContactId !== oldContactId) {
    fetchLabels();
  }
});

onMounted(fetchLabels);
</script>

<template>
  <div class="flex flex-col gap-5 px-6 py-6">
    <div
      v-if="isFetching"
      class="flex items-center justify-start h-11 text-sm text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.LABELS.LOADING') }}
    </div>

    <div v-else-if="hasLabels" class="flex flex-col gap-1">
      <button
        v-for="label in sortedLabels"
        :key="label.id"
        type="button"
        class="flex items-center w-full gap-3 px-3 py-2 text-left transition-colors rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
        :class="{ 'opacity-60 pointer-events-none': isUpdating }"
        @click="toggleLabel(label)"
      >
        <Checkbox
          :model-value="isLabelSelected(label)"
          :disabled="isUpdating"
          @click.stop
          @change="toggleLabel(label)"
        />
        <span
          class="size-2.5 rounded-sm shrink-0"
          :style="{ backgroundColor: label.color }"
        />
        <span class="min-w-0 text-sm font-medium truncate">
          {{ label.title }}
        </span>
      </button>
    </div>

    <p v-else class="text-sm leading-6 text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.LABELS.EMPTY_STATE') }}
    </p>
  </div>
</template>
